/**
 * Auszahlung/Vorschuss melden (Phase I, Kapitel 16.3).
 *
 * `mode="regular"`: an einen Abrechnungszeitraum gebunden (periodFrom/periodTo
 * fest vorgegeben, i. d. R. der aktuell angezeigte Zyklus), Betrag vorbelegt
 * mit dem berechneten Lohn, aber editierbar (z. B. wenn abweichend
 * ausgezahlt wurde). Sofort `confirmed`, keine Chef-Aktion nötig.
 *
 * `mode="advance"`: freier Betrag, kein Zeitraum, startet als `reported` und
 * wartet auf die Chef-Entscheidung.
 *
 * Foto ist in beiden Fällen optional (Kapitel 16.3: „macht eins, wenn er
 * kann"). Wird clientseitig auf ~1280px/JPEG komprimiert, siehe
 * lib/payouts.ts::compressPhotoToDataUrl.
 */

import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useCreatePayout } from '../data/payouts';
import { compressPhotoToDataUrl } from '../lib/payouts';
import { ApiError } from '../lib/api';
import { hapticError, hapticSuccess } from '../lib/haptics';

interface Props {
  mode: 'regular' | 'advance';
  period?: { from: string; to: string; label: string };
  defaultAmount?: number;
  onClose: () => void;
  onSaved: () => void;
}

export function PayoutModal({ mode, period, defaultAmount, onClose, onSaved }: Props) {
  const create = useCreatePayout();
  const [amount, setAmount] = useState(defaultAmount != null ? defaultAmount.toFixed(2) : '');
  const [note, setNote] = useState('');
  const [photoData, setPhotoData] = useState<string | null>(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onPhotoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPhotoBusy(true);
    setError(null);
    try {
      const dataUrl = await compressPhotoToDataUrl(file);
      setPhotoData(dataUrl);
    } catch {
      setError('Foto konnte nicht verarbeitet werden.');
    } finally {
      setPhotoBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const parsed = Number(amount.replace(',', '.'));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Bitte einen gültigen Betrag angeben.');
      return;
    }
    try {
      await create.mutateAsync({
        type: mode,
        amount: Math.round(parsed * 100) / 100,
        periodFrom: mode === 'regular' ? period?.from : undefined,
        periodTo: mode === 'regular' ? period?.to : undefined,
        photoData,
        note: note.trim() ? note.trim() : null,
      });
      hapticSuccess();
      onSaved();
    } catch (err) {
      hapticError();
      setError(
        err instanceof ApiError
          ? err.message
          : 'Meldung fehlgeschlagen — bitte erneut versuchen.',
      );
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 z-50"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-surface-2 rounded-t-2xl sm:rounded-lg p-4 sm:p-5 w-full max-w-md max-h-[90vh] overflow-y-auto border border-border/60">
        <h3 className="text-lg font-semibold mb-1">
          {mode === 'regular' ? 'Auszahlung bestätigen' : 'Vorschuss melden'}
        </h3>
        <p className="text-xs text-slate-400 mb-4">
          {mode === 'regular'
            ? period
              ? `Zeitraum: ${period.label}`
              : 'Aktueller Abrechnungszeitraum'
            : 'Wird dem Chef zur Bestätigung vorgelegt.'}
        </p>

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Betrag (€)</span>
            <input
              type="text"
              inputMode="decimal"
              required
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="input"
              placeholder="0,00"
            />
          </label>

          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">
              Foto vom Umschlag (optional)
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPhotoChange}
              className="text-xs text-slate-400"
            />
            {photoBusy && <p className="text-xs text-slate-500 mt-1">Foto wird verarbeitet…</p>}
            {photoData && (
              <div className="mt-2 flex items-center gap-2">
                <img
                  src={photoData}
                  alt="Vorschau"
                  className="w-16 h-16 object-cover rounded border border-border/60"
                />
                <button
                  type="button"
                  onClick={() => setPhotoData(null)}
                  className="text-xs text-red-400 hover:text-red-300"
                >
                  Entfernen
                </button>
              </div>
            )}
          </label>

          <label className="block">
            <span className="block text-sm text-slate-300 mb-1">Notiz (optional)</span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="input resize-y"
              placeholder={
                mode === 'regular'
                  ? 'z. B. Geld über eine Kollegin erhalten'
                  : 'z. B. Grund für den Vorschuss'
              }
            />
          </label>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost w-full sm:w-auto">
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={create.isPending || photoBusy}
              className="btn w-full sm:w-auto"
            >
              {create.isPending ? 'Sende…' : mode === 'regular' ? 'Bestätigen' : 'Melden'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
